import { EmptyState } from "@/components/molecules/empty-state";
import { ErrorState } from "@/components/molecules/error-state";
import { Field } from "@/components/molecules/field";
import {
  SearchableSelect,
  type SearchableSelectItem,
} from "@/components/molecules/searchable-select";
import { SettingsSection } from "@/components/molecules/settings-section";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  useNotificationTopics,
  useUpdateNotificationSetting,
} from "@/features/notifications/hooks/use-notifications";
import type {
  NotificationFrequency,
  NotificationCandidate,
  NotificationSettingPatch,
  NotificationTopicView,
} from "@/features/notifications/types";
import { OWNERS_RECIPIENT } from "@/features/notifications/types";
import { useTranslation } from "@/features/utils/i18n";
import { BellSimpleIcon, CrownSimpleIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import { PageLayout } from "../layout";

/**
 * Company > Settings > Notifications — one row per subject: is it sent, how often, and
 * to whom.
 *
 * There is no personal version of this page. Being told that the FAQ needs reviewing is
 * part of how the team divides the work, so it is set once for the company by somebody
 * who can see the whole team; picking nobody means the owners.
 *
 * Every control saves as it changes (one PATCH per subject), so there is no save
 * bar here: nothing is ever left unsaved.
 */
export function NotificationsSettingsPage() {
  const { t } = useTranslation("notifications");
  const { data, isLoading, isError, refetch } = useNotificationTopics();
  const update = useUpdateNotificationSetting();

  const topics = data?.topics || [];
  const candidates = data?.candidates || [];

  const save = async (topic: string, patch: NotificationSettingPatch) => {
    try {
      await update.mutateAsync({ topic, patch });
    } catch {
      toast.error(t("notifications.saveError", "Could not save that setting"));
    }
  };

  return (
    <PageLayout
      width="medium"
      title={t("notifications.title", "Notifications")}
      description={t(
        "notifications.description",
        "Choose what your team is told about, how often, and who receives it. Nothing is sent when there is nothing waiting.",
      )}
    >
      {isLoading ? (
        <div className="space-y-8">
          <Skeleton className="h-32 w-full rounded-lg" />
          <Skeleton className="h-32 w-full rounded-lg" />
        </div>
      ) : isError ? (
        <ErrorState onRetry={refetch} />
      ) : !topics.length ? (
        <EmptyState
          icon={BellSimpleIcon}
          title={t(
            "notifications.empty",
            "No notification is available for this company yet.",
          )}
        />
      ) : (
        <div>
          {topics.map((topic) => (
            <TopicRow
              key={topic.key}
              topic={topic}
              candidates={candidates}
              onSave={save}
            />
          ))}
        </div>
      )}
    </PageLayout>
  );
}

function TopicRow({
  topic,
  candidates,
  onSave,
}: {
  topic: NotificationTopicView;
  candidates: NotificationCandidate[];
  onSave: (topic: string, patch: NotificationSettingPatch) => void;
}) {
  const { t } = useTranslation("notifications");
  const { setting } = topic;

  // Who may receive *this* subject: the server answers, because eligibility is a
  // permission question and the permission matrix is not the front-end's to rebuild.
  // These are the company's **accesses** (a login with an address), not the team the
  // agent names — most of a team has no account to write to.
  const eligible = candidates.filter((candidate) =>
    candidate.eligible_topics.includes(topic.key),
  );

  // "Owners" is a group, not a person: it follows the role, so whoever is an owner on
  // the day the digest goes out receives it. It is also where an empty pick lands.
  const owners = eligible.filter((candidate) => candidate.is_owner);
  const items: SearchableSelectItem[] = [
    {
      id: OWNERS_RECIPIENT,
      label: t("notifications.owners", "Owners"),
      description: owners.map((owner) => owner.name || owner.email).join(", "),
      icon: <CrownSimpleIcon className="h-4 w-4" />,
    },
    ...eligible.map((candidate) => ({
      id: candidate.user_id,
      label: candidate.name || candidate.email,
      description: candidate.name ? candidate.email : undefined,
    })),
  ];

  return (
    <SettingsSection title={topic.title} description={topic.description}>
      <Field
        orientation="horizontal"
        label={t("notifications.send", "Send this notification")}
        htmlFor={`notification-${topic.key}`}
      >
        <Switch
          id={`notification-${topic.key}`}
          checked={setting.enabled}
          onCheckedChange={(enabled) => onSave(topic.key, { enabled })}
        />
      </Field>

      {setting.enabled && (
        <>
          <Field
            label={t("notifications.frequency", "Frequency")}
            htmlFor={`frequency-${topic.key}`}
            hint={
              topic.kind === "backlog" &&
              t(
                "notifications.onlyWhenPending",
                "Only sent when something is waiting.",
              )
            }
          >
            <FrequencySelect
              id={`frequency-${topic.key}`}
              value={setting.frequency}
              options={topic.frequencies}
              onChange={(frequency) => onSave(topic.key, { frequency })}
            />
          </Field>

          <Field
            label={t("notifications.recipients", "Recipients")}
            hint={
              !eligible.length
                ? t(
                    "notifications.noEligible",
                    "No user can receive this one: nobody holds the permission it needs. Invite or promote somebody under Settings > Users.",
                  )
                : t(
                    "notifications.recipientsDefault",
                    "With nobody picked, the owners receive it.",
                  )
            }
          >
            {!!eligible.length && (
              <SearchableSelect
                variant="select"
                multiple
                showBadges
                items={items}
                value={setting.recipients}
                onChange={(next) =>
                  onSave(topic.key, {
                    recipients: Array.isArray(next) ? next : [next],
                  })
                }
                placeholder={t("notifications.owners", "Owners")}
              />
            )}
          </Field>
        </>
      )}
    </SettingsSection>
  );
}

function FrequencySelect({
  id,
  value,
  options,
  onChange,
}: {
  id?: string;
  value: NotificationFrequency;
  options: NotificationFrequency[];
  onChange: (frequency: NotificationFrequency) => void;
}) {
  const { t } = useTranslation("notifications");

  return (
    <Select
      value={value}
      onValueChange={(next) => onChange(next as NotificationFrequency)}
    >
      <SelectTrigger id={id} className="w-40">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((frequency) => (
          <SelectItem key={frequency} value={frequency}>
            {t(`notifications.frequencies.${frequency}`, frequency)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
